<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'next/button/Button.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const isConnecting = ref(false);
const isFinalizing = ref(false);

const showLoader = computed(() => isConnecting.value || isFinalizing.value);
const processingMessage = computed(() =>
  isFinalizing.value
    ? t('INBOX_MGMT.ADD.WHATSAPP.DUALHOOK.FINALIZING')
    : t('INBOX_MGMT.ADD.WHATSAPP.DUALHOOK.CONNECTING')
);

const handleError = error => {
  isConnecting.value = false;
  isFinalizing.value = false;
  const errorMessage =
    parseAPIErrorResponse(error) ||
    t('INBOX_MGMT.ADD.WHATSAPP.DUALHOOK.ERROR_MESSAGE');
  useAlert(errorMessage);
};

const connect = async () => {
  try {
    isConnecting.value = true;
    const { onboarding_url: onboardingUrl } = await store.dispatch(
      'inboxes/createDualhookSession'
    );
    if (!onboardingUrl) {
      throw new Error(t('INBOX_MGMT.ADD.WHATSAPP.DUALHOOK.ERROR_MESSAGE'));
    }
    window.location.href = onboardingUrl;
  } catch (error) {
    handleError(error);
  }
};

const finalize = async sessionId => {
  try {
    isFinalizing.value = true;
    const inbox = await store.dispatch('inboxes/finalizeDualhook', {
      sessionId,
    });
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: inbox.id },
    });
  } catch (error) {
    handleError(error);
  }
};

// Statuses where Dualhook already holds an active connection for this tenant, so we
// can still recover by finalizing from the existing connection instead of restarting.
const RECOVERABLE_ERRORS = ['phone_already_connected'];

onMounted(() => {
  const { sessionId, status, error } = route.query;
  if (status === 'completed') {
    finalize(sessionId);
  } else if (status === 'failed' && RECOVERABLE_ERRORS.includes(error)) {
    finalize(sessionId);
  } else if (status === 'failed') {
    useAlert(error || t('INBOX_MGMT.ADD.WHATSAPP.DUALHOOK.ERROR_MESSAGE'));
  }
});
</script>

<template>
  <div class="h-full">
    <LoadingState v-if="showLoader" :message="processingMessage" />

    <div v-else>
      <div class="flex flex-col items-start mb-6 text-start">
        <div class="flex justify-start mb-6">
          <div
            class="flex size-11 items-center justify-center rounded-full bg-n-alpha-2"
          >
            <Icon icon="i-woot-whatsapp" class="text-n-slate-10 size-6" />
          </div>
        </div>

        <h3 class="mb-2 text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.DUALHOOK.TITLE') }}
        </h3>
        <p class="text-sm leading-[24px] text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.DUALHOOK.DESC') }}
        </p>
      </div>

      <div class="flex mt-4">
        <NextButton
          :disabled="isConnecting"
          :is-loading="isConnecting"
          faded
          slate
          class="w-full"
          @click="connect"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.DUALHOOK.SUBMIT_BUTTON') }}
        </NextButton>
      </div>
    </div>
  </div>
</template>
